import { FC, useCallback, useMemo } from 'react';

// Base steps covering 1L to 1Cr
const STANDARD_STEPS = [
  ...Array.from({ length: 10 }, (_, i) => (i + 1) * 100000), // 1L to 10L (1L increments)
  ...Array.from({ length: 5 }, (_, i) => (12 + i * 2) * 100000), // 12L to 20L (2L increments)
  3000000,
  4000000,
  5000000, // 30L, 40L, 50L (10L increments)
  7500000,
  10000000, // 75L, 1Cr (25L increments)
];

type InvestmentAmountSliderProps = {
  value: number;
  onChange: (value: number) => void;
  min: number;
  max: number;
  increment: number;
};

const formatLabel = (amount: number) => {
  if (amount >= 10000000) {
    const cr = amount / 10000000;
    return `${Number.isInteger(cr) ? cr : cr.toFixed(2)}Cr`;
  }
  const l = amount / 100000;
  return `${Number.isInteger(l) ? l : l.toFixed(1)}L`;
};

const InvestmentAmountSlider: FC<InvestmentAmountSliderProps> = ({ value, onChange, min }) => {
  const steps = useMemo(() => {
    // Filter out steps smaller than min and prepend min
    const validSteps = STANDARD_STEPS.filter(step => step > min);
    // Use Set to ensure uniqueness if min matches a step exactly
    return Array.from(new Set([min, ...validSteps]));
  }, [min]);

  const maxStepIndex = steps.length - 1;

  const getSliderStepFromAmount = useCallback(
    (amount: number) => {
      if (amount <= steps[0]) return 0;
      if (amount >= steps[steps.length - 1]) return steps.length - 1;

      const index = steps.findIndex(step => step > amount);
      if (index === -1) return steps.length - 1;

      const lowerIndex = index - 1;
      const lowerValue = steps[lowerIndex];
      const upperValue = steps[index];

      const fraction = (amount - lowerValue) / (upperValue - lowerValue);
      return lowerIndex + fraction;
    },
    [steps]
  );

  const currentStep = useMemo(
    () => getSliderStepFromAmount(value),
    [value, getSliderStepFromAmount]
  );

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const rawValue = parseFloat(event.target.value);
    // Snap to nearest step index when interacting with slider
    const snappedIndex = Math.round(rawValue);
    const validIndex = Math.min(Math.max(snappedIndex, 0), maxStepIndex);
    const amount = steps[validIndex];
    onChange(amount);
  };

  return (
    <div className="space-y-3 pt-8 pb-2">
      <div className="relative mx-3">
        <input
          type="range"
          min={0}
          max={maxStepIndex}
          step={0.01} // Fine resolution for accurate display of typed values
          value={currentStep}
          onChange={handleChange}
          className="baskets-slider relative z-10"
        />
        <div className="absolute left-0 right-0 top-1/2 -translate-y-1/2 flex justify-between pointer-events-none px-[2px]">
          {steps.map(amount => {
            return (
              <div key={amount} className="relative flex flex-col items-center group w-0">
                <span className="absolute -top-8 text-[10px] font-medium text-slate-500 whitespace-nowrap">
                  {formatLabel(amount)}
                </span>
                <span className="h-1 w-1 rounded-full bg-slate-300 mt-1" />
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default InvestmentAmountSlider;
